<script setup>

import {computed, defineProps, onMounted, ref, watch} from 'vue'
import {useStore} from 'vuex'
import createStudioEditor from '@grapesjs/studio-sdk';
import '@grapesjs/studio-sdk/style';
import {createFormData} from "@/mixins/Helpers";
import {useLocales} from "@/composables/locales";

const { locale } = useLocales()

const store = useStore()

const props = defineProps({
  id: {type: String, required: true},
  type: {type: String, default: "email"},
  template: {type: String, required: true, default: "<h1>Content</h1>"},
})

const loading = ref(false)

const currentTemplate = computed(() => store.getters['builder/getCurrentTemplate'])
const templateKey = computed(() => store.getters['events/getTemplateKey'])


watch(() => templateKey.value, (value) => {
  if (value) {
    initBuilder()
  }
})

function loadTemplateKey(){
  loading.value = true
  store.dispatch('events/loadTemplateKey')
      .catch(() => {})
      .finally(() => loading.value = false)
}

const loadBuilderFiles = async () => {
  const { data } = await store.dispatch('builder/loadBuilderFiles');
  return data
}

const uploadAssets = async (files) => {
  const formData = createFormData({files})
  const { data }  = await store.dispatch('builder/uploadBuilderFiles', formData);
  return data
}
const deleteAssets = async ({ assets }) => {
  const data = assets.map(asset => asset.getSrc())
  await store.dispatch('builder/deleteBuilderFiles', {files : data});
}

const saveTemplate = async ({ editor, project }) => {
  const files = await editor.runCommand('studio:projectFiles', { styles: 'inline' })
  const firstPage = files.find(file => file.mimeType === 'text/html');
  const content = firstPage.content;
  const data = {
    id: props.id,
    content
  }
  await store.dispatch('builder/saveTemplate', data);
}


const loadTemplateData = async () => {
  return await store.dispatch('builder/getTemplate', props.id);
}

function initBuilder() {
  createStudioEditor({
    container: '#builder-' + props.id,
    root: '#builder-' + props.id,
    licenseKey: import.meta.env.VITE_APP_BUILDER_LICENCE_KEY,
    plugins: [],
    project: {
      default: {
        pages: [
          { name: "Home", component: "<h3> Content default </h3>" },
        ],
      },
    },
    assets: {
      storageType: 'self',
      onUpload: async ({ files }) => {
        return await uploadAssets(files)
      },
      onDelete: async ({ assets }) => {
        await deleteAssets({assets})
      },
      onLoad:  async () => {
        const data = await loadBuilderFiles()
        return data.map((file, i) => ({
          ...file
        }))
      },
    },
    storage: {
      type: 'self',
      onSave: saveTemplate,
      onLoad: async () => {
        const response = await loadTemplateData()
        return {
          project: {
            pages: [
              { name: "Home", component: currentTemplate.value.content },
            ]
          }
        }
      },
      autosaveChanges: 100,
      // autosaveIntervalMs: 10000
    },
    onReady: async (editor) => {
      const variables = currentTemplate.value && currentTemplate.value.variables ? currentTemplate.value.variables : []
      if(variables.length > 0) {
        variables.forEach((value) => {
          editor.BlockManager.add(`var-${value.value}`, {
            label: `${value?.[locale.value]}`,
            category: 'Variables',
            content: `${value.content}`,
          });
        });

      }

    },
  });
}

onMounted(() => {
  loadTemplateKey()
})

</script>

<template>
  <div :id="'builder-'+id" style="height: 100vh"></div>
</template>

<style scoped>
</style>